import { useCallback, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { Spinner, ToastProvider } from '@bbpos/ui';
import { AppRouter } from './router';
import { useAuthStore } from '@/stores/use-auth-store';
import { useRealtimeNotifications } from '@/hooks/use-realtime-notifications';
import { IdleSessionGuard } from '@/components/IdleSessionGuard';
import { authService } from '@/services/auth.service';

// G2.9: app-shell-level realtime hydrator so the notification bell stays
// live regardless of which page the user is on.
function RealtimeNotificationsHydrator() {
  useRealtimeNotifications();
  return null;
}

// FR-AUTH-020: warn, then sign out, after the inactivity timeout.
function IdleLogout() {
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const onExpire = useCallback(() => {
    void logout().then(() => navigate('/login', { replace: true }));
  }, [logout, navigate]);
  return <IdleSessionGuard onExpire={onExpire} keepAlive={authService.getMe} />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppContent() {
  const hydrate = useAuthStore((s) => s.hydrate);
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  if (!isHydrated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner variant="page" size="md" label="Starting Orio360 Admin..." />
      </div>
    );
  }

  return (
    <>
      <RealtimeNotificationsHydrator />
      {isAuthenticated && <IdleLogout />}
      <AppRouter />
    </>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
